<template>
  <div class="fixed-app flex flex-col">
    <!-- ヘッダー部 -->
    <div class="w-full bg-white border-b-[1px] border-gray200">
      <div class="flex w-full items-center h-[47px] pl-5 pr-9">
        <!-- 戻るボタン -->
        <img
          src="@/assets/Icon_Left_grayV2.svg"
          class="w-6 h-6"
          @click="goToBackPage()"
        />
        <!-- 投稿者ID -->
        <div
          v-if="existContributor && !isFetchFailed"
          class="w-full text-W7 text-[15px] leading-[21px] truncate"
        >
          {{ contributor.contributorId }}
        </div>
      </div>
    </div>
    <!-- Topに戻るボタン -->
    <ScrollTopButton
      targetId="other-user-my-page-contents"
      class="fixed bottom-[72px] right-5"
    />
    <!-- 投稿者情報含む投稿一覧 -->
    <div
      id="other-user-my-page-contents"
      class="flex-1 overflow-y-auto overscroll-none"
      ref="scrollContainer"
    >
      <div class="h-full bg-gray200">
        <PullToRefreshAndPagination
          @refresh="handleRefresh"
          @pagination="handlePagination"
          @scroll="handleScroll"
          ref="commentList"
        >
          <!-- 投稿データ取得失敗 -->
          <template v-if="isFetchFailed">
            <div class="mt-16">
              <div class="text-W4 text-gray400 text-[13px] leading-[23px]">
                投稿を取得できませんでした
              </div>
              <div class="my-4 flex justify-center">
                <img :src="errorSrc" class="h-[140px] w-[140px]" />
              </div>
              <TheButton
                text="再読み込み"
                class="m-auto w-[123px]"
                @click-button="updateContributorAndCommentList()"
              />
            </div>
          </template>
          <!-- 投稿データ取得成功 -->
          <template v-else-if="existContributor">
            <!-- ユーザー基本情報 -->
            <UserDescription :contributor="contributor" />
            <!-- 横棒 -->
            <div class="h-2 w-full" />
            <!-- 投稿一覧 -->
            <div
              v-for="(comment, index) in commentList"
              :key="comment.commentId"
            >
              <div v-if="index !== 0" class="h-2 w-full bg-gray200" />
              <CommentCard
                :comment="comment"
                @on-click-comment="goToCommentDetail(comment)"
              />
            </div>
          </template>
        </PullToRefreshAndPagination>
        <div class="h-[80px] w-full bg-gray200" />
      </div>
    </div>
  </div>
</template>
<script>
import {navigateTo, goBackDokoiku, META_TAB} from '@/router'
import TheButton from '@/components/atoms/TheButton.vue'
import CommentCard from '@/components/organisms/dokoiku/CommentCard.vue'
import UserDescription from '@/components/organisms/dokoiku/UserDescription.vue'
import PullToRefreshAndPagination from '@/components/molecules/PullToRefreshAndPagination.vue'
import Util from '@/mixins/util'
import NativeUtil from '@/mixins/nativeUtil.js'
import ScrollTopButton from '@/components/atoms/ScrollTopButton.vue'

const FIRST_PAGE = 0
const OtherUserMyPage = {
  name: 'OtherUserMyPage',
  components: {
    TheButton,
    CommentCard,
    UserDescription,
    PullToRefreshAndPagination,
    ScrollTopButton,
  },

  data() {
    return {
      errorSrc: Util.methods.getErrorAssetsSrc(), // 検索失敗時の画像パス
    }
  },
  mounted() {
    // スクロールイベント追加
    this.addScrollEvent()
  },
  async activated() {
    const contributor = this.$route.query?.ContributorId
    // QueryパラメータにContributorIdが指定されている場合は、
    // リンク遷移となるため、投稿情報を読み込む準備を行う。
    if (contributor) {
      this.$store.commit('DokoikuStore/setOtherUserMyPageInfo', {
        key: 'selectedContributorId',
        value: contributor,
      })
      // フッタータブ情報を切り替え
      this.$store.commit('updateFooterTab', this.$config.FOOTER_ID_DOKOIKU)
    }
    const prevPageMeta = this.$route.params.prevMeta
    const isGoBack = this.$route.params.isGoBack || false

    // Queryパラメータで指定された場合または、ドコイク機能の画面内遷移で、前に進む遷移を行った場合は情報の更新を行う
    if (contributor || (!isGoBack && prevPageMeta.tab == META_TAB.DOKOIKU)) {
      await this.updateContributorAndCommentList()
    }

    // アクティブになったタイミングで、スクロール位置を元の状態に切り替える。
    this.$nextTick(() => {
      this.$refs.scrollContainer.scrollTop = this.scrollPosition
    })
  },
  computed: {
    /**
     * 投稿者情報のデータ有無
     */
    existContributor() {
      const zero = 0
      return Object.keys(this.contributor).length !== zero
    },
    /** 投稿者情報 */
    contributor: {
      get() {
        return this.$store.state.DokoikuStore.otherUserMyPage.contributor
      },
      set(value) {
        this.$store.commit('DokoikuStore/setOtherUserMyPageInfo', {
          key: 'contributor',
          value: value,
        })
      },
    },
    /** 記事リスト */
    commentList: {
      get() {
        return this.$store.state.DokoikuStore.otherUserMyPage.commentList
      },
      set(value) {
        let newList
        if (this.offset === FIRST_PAGE) {
          // 先頭ページ読み込みの場合、新しく置き換え
          newList = value
        } else {
          // 続きのページ読み込みの場合、タイムライン追加
          const currentCommentList =
            this.$store.state.DokoikuStore.otherUserMyPage.commentList
          const tmpCommentList = structuredClone(currentCommentList)
          tmpCommentList.push(...value)
          newList = tmpCommentList
        }

        this.$store.commit('DokoikuStore/setOtherUserMyPageInfo', {
          key: 'commentList',
          value: newList,
        })
      },
    },
    /** 取得位置 */
    offset: {
      get() {
        return this.$store.state.DokoikuStore.otherUserMyPage.offset
      },
      set(value) {
        this.$store.commit('DokoikuStore/setOtherUserMyPageInfo', {
          key: 'offset',
          value: value,
        })
      },
    },
    /** スクロール位置 */
    scrollPosition: {
      get() {
        return this.$store.state.DokoikuStore.otherUserMyPage.scrollPosition
      },
      set(value) {
        this.$store.commit('DokoikuStore/setOtherUserMyPageInfo', {
          key: 'scrollPosition',
          value: value,
        })
      },
    },
    /** 記事取得失敗有無 */
    isFetchFailed: {
      get() {
        return this.$store.state.DokoikuStore.otherUserMyPage.isFetchFailed
      },
      set(value) {
        this.$store.commit('DokoikuStore/setOtherUserMyPageInfo', {
          key: 'isFetchFailed',
          value: value,
        })
      },
    },
    /** 投稿者ID */
    contributorId: {
      get() {
        return this.$store.state.DokoikuStore.otherUserMyPage
          .selectedContributorId
      },
    },
  },
  watch: {
    '$route.query': {
      async handler(newQuery) {
        if (newQuery?.ContributorId) {
          this.$store.commit('DokoikuStore/setOtherUserMyPageInfo', {
            key: 'selectedContributorId',
            value: newQuery?.ContributorId,
          })
          await this.updateContributorAndCommentList()
        }
      },
    },
  },
  methods: {
    /**
     * スクロールイベント追加
     */
    addScrollEvent() {
      const el = document.getElementById('other-user-my-page-contents')
      el.addEventListener('scroll', this.$refs.commentList.handleScroll)
    },
    /**
     * 投稿者情報と投稿一覧を更新する
     * 用途：画面遷移時での取得、再読み込みでの取得
     */
    async updateContributorAndCommentList() {
      // 画面遷移に過去の内容が表示されるため、初期化
      this.commentList = []
      this.contributor = {}
      this.$store.commit('startLoading')
      try {
        await this.getContributorAndCommentList()
      } catch (e) {
        const status = e?.response?.status
        if (status === this.$config.API_STATUS_CODE_SERVICE_UNAVAILABLE) {
          // メンテナンスモードの場合、エラーをスローする
          throw e
        }
      }

      this.$store.commit('endLoading')
    },

    /**
     * 投稿者,記事取得処理
     * ※投稿一覧は先頭ページ目から取得する
     */
    getContributorAndCommentList() {
      this.offset = 0
      return new Promise((resolve, reject) => {
        Promise.all([
          this.getCommentListByAuthor(this.offset),
          this.getContributor(),
        ])
          .then(([commentList, contributor]) => {
            this.isFetchFailed = false
            this.commentList = commentList.items
            this.offset = this.commentList.length
            this.contributor = contributor
            resolve()
          })
          .catch((e) => {
            this.isFetchFailed = true
            this.commentList = []
            this.contributor = {}
            reject(e)
          })
      })
    },
    /**
     * 投稿記事取得処理
     * @param {Number} offset 取得開始位置
     * @returns {Array} 取得した記事情報
     */
    getCommentListByAuthor(offset) {
      return new Promise((resolve, reject) => {
        this.$store.dispatch('DokoikuStore/fetchCommentListByAuthor', {
          success: (result) => {
            resolve(result)
          },
          failed: () => {
            reject()
          },
          error: (e) => {
            reject(e)
          },
          contributorId: this.contributorId,
          sortName: 'createAt',
          sortPattern: 'desc',
          offset: offset,
        })
      })
    },
    /**
     * 投稿者情報取得
     * @returns {Object} 取得した投稿者情報
     */
    getContributor() {
      return new Promise((resolve, reject) => {
        this.$store.dispatch('DokoikuStore/getContributor', {
          success: (result) => {
            resolve(result)
          },
          failed: () => {
            reject()
          },
          error: (e) => {
            reject(e)
          },
          contributorId: this.contributorId,
        })
      })
    },
    /**
     * リフレッシュ操作時処理
     * ページを先頭に戻し、投稿者と投稿一覧を更新
     * 用途：プルで再取得したい場合
     * @param {Function} refreshDone 終了処理
     */
    async handleRefresh(refreshDone) {
      try {
        await this.getContributorAndCommentList()
      } catch (e) {
        const status = e?.response?.status
        if (status === this.$config.API_STATUS_CODE_SERVICE_UNAVAILABLE) {
          // メンテナンスモードの場合、終了処理を行った上エラーをスローする
          refreshDone()
          throw e
        }
      }
      refreshDone()
    },
    /**
     * ページネーション操作時処理
     * @param {Function} paginationDone 終了処理 引数:スクロール対象要素ID
     */
    async handlePagination(paginationDone) {
      // ページを更新し、投稿一覧を追加
      const prevOffset = structuredClone(this.offset)
      try {
        const {items} = await this.getCommentListByAuthor(this.offset)
        /* eslint-disable-next-line no-magic-numbers*/
        if (items.length === 0) throw new Error()

        this.commentList = items
        this.offset = this.commentList.length
      } catch (e) {
        // 取得失敗時は元の取得位置に戻す
        this.offset = prevOffset
        const status = e?.response?.status
        if (status === this.$config.API_STATUS_CODE_SERVICE_UNAVAILABLE) {
          // メンテナンスモードの場合、終了処理を行った上エラーをスローする
          paginationDone()
          throw e
        }
      }
      paginationDone('other-user-my-page-contents')
    },
    /**
     * タイムラインスクロール処理
     * @param {Number} scrollTop スクロール位置
     */
    handleScroll(scrollTop) {
      this.scrollPosition = scrollTop
    },
    /**
     * 投稿詳細画面に遷移
     */
    goToBackPage() {
      goBackDokoiku(this.$route.name)
    },
    /**
     * コメント詳細画面へ遷移
     * @param {Object} comment 選択した記事
     */
    goToCommentDetail(comment) {
      this.$store.commit('DokoikuStore/initCommentDetailInfo')
      this.$store.commit('DokoikuStore/setCommentDetailInfo', {
        key: 'selectedComment',
        value: comment,
      })
      this.$store.dispatch('PageControl/saveDokoikuHistoryPage', this.$route)
      navigateTo({name: this.$config.DISPLAY_DOKOIKU_COMMENT_DETAIL})
      NativeUtil.methods.sendViewDokoikuCommentPageLogEventForGA(
        comment.contributorId,
        comment.commentId,
        comment.title
      )
    },
  },
}
export default OtherUserMyPage
</script>
<style scooped></style>
